import { AMIComponent, IncomeLimitArea } from '@bybeam/platform-types';
import Dropdown from '@platform-ui-common/components/Dropdown/index.js';
import RadioGroup from '@platform-ui-common/components/RadioGroup/index.js';
import TextInput from '@platform-ui-common/components/TextInput/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import {
  DispatchFieldChange,
  GenericFormData,
  fieldUpdate,
} from '@platform-ui-common/hooks/useForm/index.js';
import { formatCurrency } from '@platform-ui-common/utilities/format.js';
import { RadioButtonOptions } from '@platform-ui/application/utils/index.js';
import { HOUSEHOLD_SIZE_ITEMS } from '@platform-ui/eligibility/utils/constants.js';
import { getMaxIncome } from '@platform-ui/eligibility/utils/index.js';
import IncomeLimitAreaAutocomplete from '../IncomeLimitAreaAutocomplete/index.js';

interface AmiCalculatorProps {
  dispatch: DispatchFieldChange<GenericFormData>;
  formData: GenericFormData;
  question: AMIComponent;
  errors?: GenericFormData;
}

export default function AmiCalculator({
  dispatch,
  formData,
  question: {
    key,
    props: { label, limitFilter, limitThreshold },
  },
  errors,
}: AmiCalculatorProps): JSX.Element {
  const completedRequiredFields = formData[`${key}.location`] && formData[`${key}.householdSize`];
  const maxIncome =
    completedRequiredFields &&
    getMaxIncome(
      formData[`${key}.location`],
      Number(formData[`${key}.householdSize`]),
      limitThreshold,
    );

  return (
    <div className="flex flex-col gap-4">
      <IncomeLimitAreaAutocomplete
        id={`${key}-location`}
        label={label}
        onChange={(area: IncomeLimitArea): void => dispatch(fieldUpdate(`${key}.location`, area))}
        value={formData[`${key}.location`]}
        filter={limitFilter}
        required
        error={errors?.[`${key}.location`] as string}
      />
      <Dropdown
        id={`${key}-household-size`}
        label="Household Size"
        value={formData[`${key}.householdSize`] ?? ''}
        required
        items={HOUSEHOLD_SIZE_ITEMS}
        onChange={(value: string): void => dispatch(fieldUpdate(`${key}.householdSize`, value))}
        fullWidth
        error={errors?.[`${key}.householdSize`] as string}
      />
      <TextInput
        id={`${key}-max-income`}
        label="Max Household Income Limit"
        value={maxIncome?.toString() ?? ''}
        type="currency"
        fullWidth
        disabled
      />
      {completedRequiredFields && (
        <div>
          <div className="flex flex-col lg:grow py-4">
            <Typography variant="body">
              Is your combined household annual income above{' '}
              {formatCurrency(Number(maxIncome ?? 0) * 100)}?
            </Typography>
          </div>
          <RadioGroup
            name={`${key}-income-verification`}
            selectedValue={formData[`${key}.isIncomeAboveLimit`]}
            onChange={(value): void => dispatch(fieldUpdate(`${key}.isIncomeAboveLimit`, value))}
            items={RadioButtonOptions.YesOrNo}
            error={!!errors?.[`${key}.isIncomeAboveLimit`]}
          />
        </div>
      )}
    </div>
  );
}
